import { CodeReferenceBlockHighlight } from "models/interfaces/code-reference-block-highlights/code-reference-block-hightlight";
import { CodeReferenceBlockHighlightRecord } from "models/view-models/code-reference-block-highlights/code-reference-block-highlight-record";
import SectionRecord from "models/view-models/section-record";
import HighlightBadgeList from "molecules/highlighter-badges/highlight-badge-list";
import CodeReferenceBlockSectionWrapper from "organisms/code-reference-block-editor/code-reference-block-section-wrapper";
import CodeReferenceHighlightViewModal from "organisms/highlight-modal/code-reference-highlight-view-modal";
import useActiveHighlightModal from "organisms/highlight-modal/useActiveHighlightModal";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { HighlightColor } from "utilities/enumerations/highlight-color";
import HighlightUtils from "utilities/highlight-utils";
import useNotebooksEnhancedContent from "utilities/hooks/domain/enhanced-content/use-notebooks-enhanced-content";
import useRegisterCodeReferenceHighlight from "utilities/hooks/domain/notebooks/code-reference-blocks/use-code-reference-highlight";

export interface CodeReferencePreviewProps {
    isEnhancedContentDisplayed?: boolean;
    referenceSection: SectionRecord | null;
    printable?: boolean;
    highlights?: CodeReferenceBlockHighlightRecord[];
    isEditMode?: boolean;
    onHighlight?: (
        highlight: CodeReferenceBlockHighlightRecord
    ) => Promise<void>;
    onUpdateHighlight?: (
        highlight: CodeReferenceBlockHighlightRecord
    ) => Promise<void>;
    onDeleteHighlight?: (highlightId: number | undefined) => Promise<void>;
}

export type HighlightProperties = Pick<
    CodeReferenceBlockHighlight,
    "highlightColor" | "note" | "id"
>;

const CSS_CLASS_NAME = "c-code-reference-block-content";

const CodeReferencePreview = ({
    referenceSection,
    highlights = [],
    onHighlight,
    onUpdateHighlight,
    onDeleteHighlight,
    printable = false,
    isEditMode = false,
    isEnhancedContentDisplayed,
}: CodeReferencePreviewProps) => {
    const sectionContentRef = useRef<HTMLDivElement>(null);
    const [newHighlight, setNewHighlight] =
        useState<CodeReferenceBlockHighlightRecord | null>(null);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);

    const {
        activeViewHighlightModal,
        setActiveViewModal,
        closeActiveViewModal,
    } = useActiveHighlightModal();

    const [activeHighlight, setActiveHighlight] =
        useState<CodeReferenceBlockHighlightRecord | null>(null);
    const [modalPosition, setModalPosition] = useState<{
        x: number;
        y: number;
    }>();

    const {
        cleanUpCurrentHighlights,
        renderCurrentHighlights,
        endingSectionIdOfActiveHighlight,
        refreshEndingSectionIdOfActiveHighlight,
        createAndRegisterHighlight,
        clearTemporaryHighlights,
        updateTemporaryHighlight,
        clearHighlightsForRecord,
    } = useRegisterCodeReferenceHighlight({ printable });

    const { updateEnhancedContentOnBlockChange } = useNotebooksEnhancedContent({
        edition: referenceSection?.publication?.edition,
        code: referenceSection?.publication?.code,
        externalIds: referenceSection?.getSectionExternalIds(),
        disabled: !isEnhancedContentDisplayed,
    });

    const handleButtonClick = () => {
        const highlightRecord = createAndRegisterHighlight(
            sectionContentRef.current,
            highlights
        );

        if (!highlightRecord) return;

        setNewHighlight(highlightRecord);
    };

    const removeTemporaryHighlight = () => {
        if (!newHighlight) return;

        clearTemporaryHighlights();
        setNewHighlight(null);
    };

    const handleColorChange = (newColor: HighlightColor, id?: number) => {
        const highlightToChange =
            highlights.find((highlight) => highlight.id === id) ?? newHighlight;
        if (!highlightToChange) {
            return;
        }

        updateTemporaryHighlight(
            highlightToChange.highlightColor,
            newColor,
            id ?? undefined,
            !id
                ? highlights.length + 1
                : highlights.length -
                      highlights.findIndex((highlight) => highlight.id === id)
        );
        if (!highlightToChange.id) {
            setNewHighlight(
                highlightToChange.with({
                    highlightColor: newColor,
                })
            );
        }
    };

    const handleHighlightClick = useCallback(
        (id: number, coordinates?: { x: number; y: number }) => {
            if (HighlightUtils.isTextSelected()) return;
            const activeHighlight = highlights.find((h) => h.id === id);
            if (!activeHighlight) return;
            setActiveHighlight(activeHighlight);
            setModalPosition(coordinates);
            setIsViewModalVisible(true);

            setActiveViewModal("viewClick");
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [highlights]
    );

    const displaySubSections = (
        subSection: SectionRecord | null
    ): JSX.Element | null => {
        if (!subSection || !subSection.id) return null;
        return (
            <CodeReferenceBlockSectionWrapper
                printable={printable}
                isEnhancedContentDisplayed={isEnhancedContentDisplayed}
                onSelectionEnd={refreshEndingSectionIdOfActiveHighlight}
                isEditMode={isEditMode}
                cssClassName={`${CSS_CLASS_NAME}__preview-content__subsection`}
                key={`sub-${subSection.id}`}
                section={subSection}
                showButton={endingSectionIdOfActiveHighlight === subSection.id}
                onSaveHighlight={handleSaveSelectedTextAsHighlight}
                onButtonClick={handleButtonClick}
                onCancel={removeTemporaryHighlight}
                onColorChange={handleColorChange}>
                {subSection?.getTitle()}
                {subSection?.getBody()}
                {subSection.subSections?.map((child) => {
                    return child ? displaySubSections(child) : null;
                })}
            </CodeReferenceBlockSectionWrapper>
        );
    };

    const handleSaveSelectedTextAsHighlight = async (
        props: HighlightProperties
    ) => {
        if (!newHighlight || !onHighlight) return;

        const highlight = newHighlight.with(props);

        await onHighlight(highlight);
        setNewHighlight(null);
        clearTemporaryHighlights();
    };

    // To be implemented
    const handleUpdateExistingHighlight = async (
        highlightToEdit: HighlightProperties
    ) => {
        if (!highlightToEdit || !onUpdateHighlight) return;
        const highlightRecordToUpdate =
            new CodeReferenceBlockHighlightRecord().with(highlightToEdit);
        await onUpdateHighlight(highlightRecordToUpdate);
    };

    const handleDeleteExistingHighlight = async (
        highlightId: number | undefined
    ) => {
        if (!highlightId || !onDeleteHighlight) return;

        const highlightToDelete = highlights.find((h) => h.id === highlightId);

        await onDeleteHighlight(highlightId);

        clearHighlightsForRecord({
            highlightId,
            color: highlightToDelete?.highlightColor,
        });
    };

    // Testing for Highlighting feature
    useEffect(() => {
        if (sectionContentRef.current && highlights) {
            renderCurrentHighlights(
                sectionContentRef.current,
                highlights,
                handleHighlightClick
            );
        }

        return () => cleanUpCurrentHighlights();
    }, [
        cleanUpCurrentHighlights,
        renderCurrentHighlights,
        highlights,
        handleHighlightClick,
    ]);

    // Sync NFPA Insights context on section change
    useEffect(() => {
        if (isEnhancedContentDisplayed) updateEnhancedContentOnBlockChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        referenceSection?.annexId,
        referenceSection?.chapterId,
        isEnhancedContentDisplayed,
    ]);
    return (
        <div
            ref={sectionContentRef}
            className={`${CSS_CLASS_NAME}__preview ${printable ? "-printable" : ""}`}>
            <div className={`${CSS_CLASS_NAME}__preview-header`}>
                <div
                    className={`${CSS_CLASS_NAME}__preview-header-publication`}>
                    <span>
                        {`NFPA ${referenceSection?.publication?.code} (${referenceSection?.publication?.edition})`}
                    </span>
                </div>
                <HighlightBadgeList
                    blockHighlights={highlights}
                    blockRefElement={sectionContentRef}
                    onSave={handleUpdateExistingHighlight}
                    onColorChange={handleColorChange}
                    onDelete={handleDeleteExistingHighlight}
                />
            </div>
            <CodeReferenceBlockSectionWrapper
                printable={printable}
                isEnhancedContentDisplayed={isEnhancedContentDisplayed}
                onSelectionEnd={refreshEndingSectionIdOfActiveHighlight}
                isEditMode={isEditMode}
                showButton={
                    endingSectionIdOfActiveHighlight === referenceSection?.id
                }
                cssClassName={`${CSS_CLASS_NAME}__section-wrapper`}
                key={`main-${referenceSection?.id}`}
                section={referenceSection}
                onSaveHighlight={handleSaveSelectedTextAsHighlight}
                onButtonClick={handleButtonClick}
                onCancel={removeTemporaryHighlight}
                onColorChange={handleColorChange}>
                <div className={`${CSS_CLASS_NAME}__preview-section-title`}>
                    {referenceSection?.title
                        ? referenceSection.getFullyQualifiedDisplayTitle()
                        : referenceSection?.title}
                </div>
                <div className={`${CSS_CLASS_NAME}__preview-content`}>
                    <div>
                        {referenceSection?.body
                            ? referenceSection.getBody()
                            : referenceSection?.body}
                        {referenceSection?.subSections?.map((subSection) => {
                            return subSection
                                ? displaySubSections(subSection)
                                : null;
                        })}
                    </div>
                </div>
            </CodeReferenceBlockSectionWrapper>

            {isViewModalVisible &&
                activeViewHighlightModal === "viewClick" &&
                sectionContentRef.current &&
                activeHighlight && (
                    <CodeReferenceHighlightViewModal
                        coordinates={modalPosition}
                        blockRefElement={sectionContentRef}
                        highlights={[activeHighlight]}
                        onClose={closeActiveViewModal}
                        onSave={handleUpdateExistingHighlight}
                        onColorChange={handleColorChange}
                        onDelete={handleDeleteExistingHighlight}
                        editType={"editFromViewClick"}
                    />
                )}
        </div>
    );
};
export default CodeReferencePreview;
